import { atom } from 'nanostores'
import { useStore } from '@nanostores/react'

/**
 * Shared dark mode state — single MutationObserver for all consumers.
 * Replaces per-component MutationObserver pattern that created N observers
 * for N canvas nodes all watching the same <html> element.
 */
export const $isDark = atom(
  typeof document !== 'undefined'
    ? document.documentElement.classList.contains('dark')
    : true
)

/** Derived theme value for components that need 'light' | 'dark' */
export const $theme = atom<'light' | 'dark'>(
  typeof document !== 'undefined'
    ? (document.documentElement.classList.contains('dark') ? 'dark' : 'light')
    : 'dark'
)

// Initialize observer once (client-side only)
if (typeof document !== 'undefined') {
  const observer = new MutationObserver(() => {
    const dark = document.documentElement.classList.contains('dark')
    $isDark.set(dark)
    $theme.set(dark ? 'dark' : 'light')
  })
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
}

/** Hook to read dark mode state reactively */
export function useIsDark(): boolean {
  return useStore($isDark)
}

/** Hook to read theme as 'light' | 'dark' */
export function useThemeMode(): 'light' | 'dark' {
  return useStore($theme)
}
